import { useRef } from "react";
import type { ReactNode } from "react";

import { Drawer, LoadingSkeleton } from "@narmi/design_system";

import PanelSectionHeading from "./PanelSectionHeading";

export interface TransactionPanelSection<T> {
  id: string;
  /**
   * Omitted by a section that draws its own heading, which is what a section needing something on
   * the heading line has to do. See PanelSectionHeading.
   */
  title?: string;
  render: (row: T) => ReactNode;
}

export interface TransactionDetailsPanelProps<T> {
  row: T | null;
  header: (row: T) => ReactNode;
  sections: TransactionPanelSection<T>[];
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  isLoading?: boolean;
  footer?: ReactNode;
}

const TransactionDetailsPanel = <T,>({
  row,
  header,
  sections,
  onClose,
  onNext,
  onPrev,
  isLoading = false,
  footer,
}: TransactionDetailsPanelProps<T>) => {
  // Keeps content rendered during the Drawer close animation after row goes null.
  const lastRowRef = useRef<T | null>(null);
  if (row !== null) {
    lastRowRef.current = row;
  }
  const displayRow = row ?? lastRowRef.current;

  if (displayRow === null) {
    return null;
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <div data-testid="transaction-details-panel-loading">
          <LoadingSkeleton isLoading content="headerText" size="large" />
          <LoadingSkeleton isLoading content="paragraph" lines={4} />
        </div>
      );
    }

    return (
      <>
        {header(displayRow)}
        {sections.map((section) => {
          // A section that renders nothing is dropped along with its heading. Otherwise a
          // row-dependent section, such as BAI2 detail, would leave an empty titled box on
          // every row it does not apply to.
          const content = section.render(displayRow);
          if (content === null || content === undefined || content === false) {
            return null;
          }

          return (
            <section
              key={section.id}
              className="margin--top--xl"
              data-testid={`transaction-details-panel-section-${section.id}`}
            >
              {section.title && <PanelSectionHeading title={section.title} />}
              {content}
            </section>
          );
        })}
      </>
    );
  };

  return (
    <Drawer
      isOpen={row !== null}
      onUserDismiss={onClose}
      onNext={onNext}
      onPrev={onPrev}
      position="right"
      depth="524px"
      paddingSize="xl"
      footer={footer}
      testId="transaction-details-panel"
    >
      {renderBody()}
    </Drawer>
  );
};

export default TransactionDetailsPanel;
